// Models
import {
  AdvancedModelStep,
  DetectionModel,
  DetectionModelMetadata,
  DetectionModelTemplateType,
  DetectionModelValidation,
  ModelsState,
  ModelValidationPayload,
  ScenarioCategory,
  ScenarioCategoryConfig,
  SimpleModelStep,
  StepPayload,
} from 'app/modules/detectionModels/models';
import {
  CompositeQueryTreeNode,
  CompositeTypes,
  ConstantValue,
  DmDurationUnits,
  DmObjectToFlag,
  DurationValue,
  FieldValue,
  IRSchema,
  MatchlistFieldValue,
  UiInputs,
  ValueTypes,
  VariableValue,
} from 'app/modules/rulesAdvanced/models';

// Helpers
import {
  validateSimpleModelMetadata,
  validateSimpleModelScenario,
  validateSimpleModelScenarioContent,
  validateValidationPicker,
} from 'app/modules/detectionModels/stepValidations';

// Utils
import deepFreeze from 'app/shared/utils/deepFreeze';

import { consoleError } from 'app/shared/utils/console';
import {
  DEFAULT_DATA_MIGRATION_LAG,
  initialCurrentRule,
} from 'app/modules/rules/constants';
import { format, sub, subMinutes } from 'date-fns';
import {
  U21ButtonGroupButtonProps,
  U21SelectOptionProps,
  U21ChipColor,
} from 'app/shared/u21-ui/components';
import { SymbolFieldValue } from 'app/modules/rulesAdvanced/lib/SymbolTable';
import { CytoscapeOptions } from 'cytoscape';
import {
  BASE_CYTOSCAPE_OPTIONS,
  BASE_EDGE_STYLES,
  BASE_LABEL_STYLES,
  BASE_NODE_STYLES,
} from 'app/shared/components/Graphs/constants';
import { CONNECTION_TYPE_TO_ICON } from 'app/modules/detectionModels/components/scenarioWidgets/constants';
import { User } from 'app/shared/components/Graphs/svgs';
import { RealTimeMonitoringCount } from 'app/modules/rules/types/responses';
import {
  PaginatedValidationAlerts,
  RtrDecision,
  RuleStatuses,
} from 'app/modules/rules/models';
import {
  AndOrCondition,
  Condition,
  DropdownBuilderFormValues,
  ScenarioAndOrCondition,
} from 'app/modules/detectionModels/components/DropdownBuilder/models';
import { AdvancedCondition } from 'app/modules/rulesAdvanced/components/RuleRepresentationForm/models';
import {
  TablerIcon,
  IconCircleCheck,
  IconBan,
  IconAlertTriangle,
  IconShieldLock,
} from '@u21/tabler-icons';

const today = new Date();
const endTime = format(
  subMinutes(today, DEFAULT_DATA_MIGRATION_LAG),
  'dd MMM yyyy HH:00',
);
const startTime = format(sub(today, { days: 30 }), 'dd MMM yyyy HH:00');

export const EMPTY_VALIDATION_PAYLOAD: ModelValidationPayload = {
  start_time: startTime,
  end_time: endTime,
  execution_frequency: '',
  execution_frequency_behavior: null,
};

export const EMPTY_MODEL_METADATA: DetectionModelMetadata = {
  title: '',
  description: '',
  customer_support_involved: false,
  tags: [],
  queue: -1,
  runs_on_org_id: null,
  visible_to_child_org: false,
};

export const EMPTY_MODEL_VALIDATION: DetectionModelValidation = {
  id: -1,
  scenario: '',
  scenario_type: '',
  tags: [],
  queue: -1,
  runs_on_org_id: null,
  title: '',
  customer_support_involved: false,
  description: '',
  decision: undefined,
  execution_frequency: '',
  execution_frequency_behavior: null,
  created_at: '',
  updated_at: '',
  completed_at: '',
  status: '',
  deploy_end_date: '',
  deploy_start_date: '',
  derived_from_rule: null,
  created_by_readable: '',
  validation_id: null,
  exec_range_start: '',
  exec_range_end: '',
  override_qbc: null,
  progress: {
    cur_processing_window: -1,
    num_windows: -1,
  },
  validation_result: {
    hit_key_field: '',
    hit_value_field: '',
    metrics: [],
    rule_validation_histograms: {
      in: [],
      out: [],
    },
    rule_validation_id: -1,
    rule_validation_results: [],
    rule_validation_windows: [],
    status: '',
    validation_histogram: null,
  },
  content: {
    scenario: {
      filters: {},
      parameters: {},
      name: '',
    },
  },
  template: null,
  visible_to_child_org: false,
  friendly_errors: null,
  results: null,
};

export const SIMPLE_MODEL_STEPS: { [key in SimpleModelStep]: StepPayload } = {
  SCENARIO_PICKER: {
    label: 'Scenario Picker',
    canContinue: validateSimpleModelScenario,
    nextStep: 'SCENARIO_BUILDER',
  },
  SCENARIO_BUILDER: {
    label: 'Rule Content',
    canContinue: validateSimpleModelScenarioContent,
    nextStep: 'METADATA_PICKER',
  },
  METADATA_PICKER: {
    label: 'Description',
    canContinue: validateSimpleModelMetadata,
    nextStep: 'VALIDATION_PICKER',
  },
  VALIDATION_PICKER: {
    label: 'Time Window',
    canContinue: validateValidationPicker,
    nextStep: null,
  },
};

export const SIMPLE_MODEL_STEPS_LIST = ((navigationConfig: {
  [key in SimpleModelStep]: StepPayload;
}): {
  id: SimpleModelStep;
  label: string;
}[] => {
  const steps: SimpleModelStep[] = [];

  const allSteps = Object.keys(navigationConfig) as SimpleModelStep[];
  const nextSteps = Object.values(navigationConfig).map(
    (stepConfig) => stepConfig.nextStep,
  );

  const initialStep = allSteps.filter((step) => !nextSteps.includes(step));
  if (initialStep.length !== 1) {
    // There's an error in the config, make sure we don't have recursive steps
    consoleError('Unable to get initial step from config');
    return [];
  }

  let currentStep: SimpleModelStep | null = initialStep[0];
  let { nextStep } = navigationConfig[currentStep];
  while (currentStep !== null) {
    steps.push(currentStep);
    currentStep = nextStep;
    nextStep = navigationConfig[currentStep || '']?.nextStep;
  }

  return steps.map((id) => ({
    id,
    label: navigationConfig[id].label,
  }));
})(SIMPLE_MODEL_STEPS);

export const FLAGGED_OBJECT_PARAMETER = 'alert_group_by_policy';

export const DEFAULT_SCENARIO_CATEGORY: ScenarioCategory = 'OTHER';

export const DEFAULT_CATEGORY_ARRAY = [
  { category: 'ANOMALY', id: 1 },
  { category: 'LIST', id: 2 },
  { category: 'CARDINALITY', id: 3 },
  { category: 'STATISTICS', id: 4 },
  { category: 'STRUCTURING', id: 5 },
  { category: 'CHECK_FRAUD', id: 6 },
  { category: 'OTHER', id: 7 },
];

export const SCENARIO_CATEGORY_CONFIG: ScenarioCategoryConfig = {
  STRUCTURING: {
    label: 'Structuring/Smurfing',
  },
  ANOMALY: {
    label: 'Anomaly Detection',
  },
  CARDINALITY: {
    label: 'Cardinality',
  },
  STATISTICS: {
    label: 'Statistics',
  },
  BLACKLIST: {
    // NOTE: for legacy support
    label: 'Lists',
  },
  LIST: {
    label: 'Lists',
  },
  CHECK_FRAUD: {
    label: 'Check Fraud',
  },
  ACH_FRAUD: {
    label: 'ACH Fraud',
  },
  OTHER: {
    label: 'Other',
  },
};

export const EMPTY_DETECTION_MODEL: DetectionModel = { ...initialCurrentRule };

export const ADVANCED_MODEL_STEPS: {
  id: AdvancedModelStep;
  label: string;
}[] = [
  {
    id: 'TEMPLATE_PICKER',
    label: 'Select Template',
  },
  {
    id: 'ADVANCED_BUILDER',
    label: 'Define Rule',
  },
  {
    id: 'METADATA_PICKER',
    label: 'Rule Detail',
  },
  {
    id: 'VALIDATION_PICKER',
    label: 'Time Window',
  },
];

export const EMPTY_RULE_CONDITION_GROUP: CompositeQueryTreeNode = {
  type: CompositeTypes.RULE_CONDITION_COMPOSITE,
  operands: [],
  operator: 'AND',
};

export const EMPTY_IR_RULE: IRSchema = {
  facts: [],
  rule_condition: {
    type: CompositeTypes.RULE_CONDITION_COMPOSITE,
    operands: [],
    operator: 'AND',
  },
  object: 'ENTITY',
};

export const DEFAULT_GET_SAMPLE_ALERTS_RESPONSE: PaginatedValidationAlerts = {
  count: 0,
  alerts: [],
};

export const POPULARITY_THRESHOLD = 70;

export const NEW_TEMPLATE_TYPES: DetectionModelTemplateType[] = [
  'SCAMS',
  'FINCEN',
  'FINANCIAL_INSTITUTION',
  'STRUCTURING',
  'CASH',
  'MONETARY_INSTRUMENT',
  'LOAN',
  'WIRE',
  'TRAFFICKING',
  'TERRORISM_FINANCING',
  'DRUG_TRAFFICKING',
  'SEXTORTION',
  'FOREIGN_NETWORK',
];

export const TEMPLATE_TYPE_TO_TEXT: Record<DetectionModelTemplateType, string> =
  {
    ABNORMAL_VOLUME: 'Abnormal volume',
    HISTORICAL_DEVIATION: 'Historical deviation',
    SMURFING: 'Smurfing',
    LAYERING: 'Layering',
    SCAMS: 'Scams',
    FINCEN: 'FinCEN',
    FINANCIAL_INSTITUTION: 'Financial institution',
    STRUCTURING: 'Structuring',
    CASH: 'Cash',
    MONETARY_INSTRUMENT: 'Monetary instrument',
    LOAN: 'Loan',
    WIRE: 'Wire',
    TRAFFICKING: 'Trafficking',
    TERRORISM_FINANCING: 'Terrorism financing',
    DRUG_TRAFFICKING: 'Drug trafficking',
    SEXTORTION: 'Sextortion',
    FOREIGN_NETWORK: 'Foreign network',
    '': 'All',
  };

export const EMPTY_VALIDATE_MODEL_PAYLOAD = {
  contents: {
    execution_frequency_behavior: null,
    scenario: {
      scenario_name: 'scenario_name',
      parameters: {
        $time_window: 'time_window',
        $window_stride: 'window_stride',
        alert_group_by_policy: 'alert_group_by_policy',
        count_field: 'count_field',
        group_type: 'group_type',
        txn_events: 'txn_events',
        param: {},
      },
      filters: {},
    },
    execution_frequency: 'execution_frequency',

    // TODO: to be removed. see modules/detectionModels/models.ts line 415
    query_tree: {},
    inclusion_tags: [],
    inclusion_tag_names: [],
    exclusion_tags: [],
    exclusion_tag_names: [],
    specification: undefined,
  },
  exec_range_start: 'exec_range_start',
  exec_range_end: 'exec_range_end',
  tag_ids: [],
  alert_queue_id: null,
  runs_on_org_id: null,
  metadata: {
    title: 'title',
    description: 'description',
    customer_support_involved: false,
    parentRuleId: undefined,
    exec_start_date_time: 'execStartDatetime',
    exec_end_date_time: 'execEndDatetime',
  },
  template_id: undefined,
  visible_to_child_org: undefined,
  is_synchronous: false,
};

export const INITIAL_EDITING_SIMPLE_DETECTION_MODEL = {
  metadata: { ...EMPTY_MODEL_METADATA },
  validationPayload: { ...EMPTY_VALIDATION_PAYLOAD },
  objectToFlag: null,
  scenario: null,
};

export const initialState: Readonly<ModelsState> = {
  detectionModelsPerformance: {
    series: [],
    metrics: [],
  },
  validateModelPayload: EMPTY_VALIDATE_MODEL_PAYLOAD,
  editingSimpleDetectionModel: INITIAL_EDITING_SIMPLE_DETECTION_MODEL,
  editingAdvancedDetectionModel: {
    template: null,
    irSchema: { ...EMPTY_IR_RULE },
    metadata: { ...EMPTY_MODEL_METADATA },
    validationPayload: {
      ...EMPTY_VALIDATION_PAYLOAD,
    },
  },
  scenarioConfigs: {},
  detectionModelValidation: { ...EMPTY_MODEL_VALIDATION },
  detectionModelValidationMetrics: {
    id: -1,
    metrics: [],
  },
  detectionModelTemplates: {
    templates: [],
    count: 0,
  },
};

// to ensure initialState is readonly
deepFreeze(initialState);

export const DM_STATUS_TO_COLOR: Record<string, U21ChipColor> = {
  ACTIVE: 'success',
  ARCHIVED: 'error',
  COMPLETE: 'success',
  EXPIRED: 'error',
  FAILED: 'error',
  IN_USE: 'success',
  NOT_IN_USE: 'default',
  PENDING: 'info',
  QUERY_TIMEOUT: 'error',
};

export const enum EventSubTypes {
  TXN_EVENT = 'TXN_EVENT',
  ACTION_EVENT = 'ACTION_EVENT',
}

export const OBJECT_TO_FLAG_TO_MODEL: Record<DmObjectToFlag, string> = {
  ACTION_EVENT: 'action_event',
  DEVICE: 'device',
  ENTITY: 'entity',
  INSTRUMENT: 'instrument',
  TXN_EVENT: 'txn_event',
};

export enum RtmRuleBuilderFormFields {
  EVENT_SUB_TYPES = 'eventSubTypes',
  OBJECT_TO_FLAG = 'objectToFlag',
  RTR_TYPE = 'rtrType',
  DROPDOWN_BUILDER_FORM_VALUES = 'dropdownBuilderFormValues',
  BEHAVIOR_OVER_TIME_DROPDOWN_BUILDER_FORM_VALUES = 'behaviorOverTimeDropdownBuilderFormValues',
}

export const DROPDOWN_BUILDER_REMOVABLE_COMPONENT_TYPES: string[] = [
  CompositeTypes.RULE_CONDITION_COMPOSITE,
  'VARIABLE',
];
export enum DropdownBuilderUiTypes {
  CONSTANT = 'CONSTANT',
  FIELD = 'FIELD',
  PERCENTAGE = 'MUL',
  AVERAGE = 'AVERAGE',
  MATCHLIST = 'MATCHLIST',
  EMPTY = 'EMPTY',
}

export const DROPDOWN_BUILDER_UI_TYPES_MAP: Record<
  DropdownBuilderUiTypes,
  U21SelectOptionProps
> = {
  [DropdownBuilderUiTypes.CONSTANT]: {
    text: 'input a value',
    value: DropdownBuilderUiTypes.CONSTANT,
  },
  [DropdownBuilderUiTypes.FIELD]: {
    text: 'select a field',
    value: DropdownBuilderUiTypes.FIELD,
  },
  [DropdownBuilderUiTypes.PERCENTAGE]: {
    text: 'find a percentage',
    value: DropdownBuilderUiTypes.PERCENTAGE,
  },
  [DropdownBuilderUiTypes.AVERAGE]: {
    text: 'find an average',
    value: DropdownBuilderUiTypes.AVERAGE,
  },
  [DropdownBuilderUiTypes.MATCHLIST]: {
    text: 'select a matchlist',
    value: DropdownBuilderUiTypes.MATCHLIST,
  },
  [DropdownBuilderUiTypes.EMPTY]: {
    text: 'check if empty or not',
    value: DropdownBuilderUiTypes.EMPTY,
  },
};

const NUMBER_DROPDOWN_BUILDER_UI_TYPE_OPTIONS: U21SelectOptionProps[] = [
  DROPDOWN_BUILDER_UI_TYPES_MAP.CONSTANT,
  DROPDOWN_BUILDER_UI_TYPES_MAP.FIELD,
  DROPDOWN_BUILDER_UI_TYPES_MAP.MUL,
  DROPDOWN_BUILDER_UI_TYPES_MAP.AVERAGE,
  DROPDOWN_BUILDER_UI_TYPES_MAP.EMPTY,
];

const STRING_OR_DATE_UI_TYPE_OPTIONS: U21SelectOptionProps[] = [
  DROPDOWN_BUILDER_UI_TYPES_MAP.CONSTANT,
  DROPDOWN_BUILDER_UI_TYPES_MAP.FIELD,
  DROPDOWN_BUILDER_UI_TYPES_MAP.EMPTY,
];

const PERCENTAGE_UI_TYPE_OPTIONS: U21SelectOptionProps[] = [
  DROPDOWN_BUILDER_UI_TYPES_MAP.FIELD,
  DROPDOWN_BUILDER_UI_TYPES_MAP.AVERAGE,
];

export const DROPDOWN_BUILDER_NEXT_STEP_OPTIONS: Record<
  string,
  U21SelectOptionProps[]
> = {
  date: STRING_OR_DATE_UI_TYPE_OPTIONS,
  datetime: STRING_OR_DATE_UI_TYPE_OPTIONS,
  text: STRING_OR_DATE_UI_TYPE_OPTIONS,
  string: STRING_OR_DATE_UI_TYPE_OPTIONS, // String is only here to support legacy. Use text instead.
  enum: STRING_OR_DATE_UI_TYPE_OPTIONS,
  list: STRING_OR_DATE_UI_TYPE_OPTIONS,
  boolean: STRING_OR_DATE_UI_TYPE_OPTIONS,
  [DropdownBuilderUiTypes.PERCENTAGE]: PERCENTAGE_UI_TYPE_OPTIONS,
  number: NUMBER_DROPDOWN_BUILDER_UI_TYPE_OPTIONS,
};

export enum StringOperators {
  UPPERCASE = 'UPPERCASE',
  LOWERCASE = 'LOWERCASE',
  CAPITALIZE = 'CAPITALIZE',
  TRIM = 'TRIM',
  SANITIZE = 'SANITIZE',
  FUZZY_SCORE = 'FUZZY_SCORE',
}

export enum MathOperators {
  ADD = 'ADD',
  SUB = 'SUB',
  MUL = 'MUL',
  DIV = 'DIV',
  MODULO = 'MODULO',
  LN = 'LN',
  ABS = 'ABS',
  LOG = 'LOG',
  EXP = 'EXP',
  SQRT = 'SQRT', // see DmOperator
}

export const MATH_OPERATORS_MAP: Record<MathOperators, string> = {
  [MathOperators.ADD]: '+',
  [MathOperators.SUB]: '-',
  [MathOperators.MUL]: '*',
  [MathOperators.DIV]: '/',
  [MathOperators.MODULO]: '%',
  [MathOperators.LN]: 'ln',
  [MathOperators.ABS]: 'abs',
  [MathOperators.LOG]: 'log',
  [MathOperators.EXP]: 'exp',
  [MathOperators.SQRT]: 'sqrt',
};

export enum ConditionalOperators {
  GT = 'GT',
  LT = 'LT',
  LE = 'LE',
  GE = 'GE',
  EQ = 'EQ',
  ANY_EQ = 'ANY_EQ',
  NEQ = 'NEQ',
  NONE_EQ = 'NONE_EQ',
  IN = 'IN',
  ANY_IN = 'ANY_IN',
  NOT_IN = 'NOT_IN',
  NONE_IN = 'NONE_IN',
  LIKE = 'LIKE',
  ILIKE = 'ILIKE',
  ANY_LIKE = 'ANY_LIKE',
  NOT_LIKE = 'NOT_LIKE',
  NOT_ILIKE = 'NOT_ILIKE',
  NONE_LIKE = 'NONE_LIKE',
  BETWEEN = 'BETWEEN',
  IS = 'IS',
  IS_EMPTY = 'IS_EMPTY',
  IS_NOT_EMPTY = 'IS_NOT_EMPTY',
  ANY_IS_EMPTY = 'ANY_IS_EMPTY',
  NONE_IS_EMPTY = 'NONE_IS_EMPTY',
  ILIKE_CONTAINS = 'ILIKE_CONTAINS',
  NOT_ILIKE_CONTAINS = 'NOT_ILIKE_CONTAINS',
  STARTS_WITH = 'STARTS_WITH',
  NOT_STARTS_WITH = 'NOT_STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
  NOT_ENDS_WITH = 'NOT_ENDS_WITH',
}

export const CONDITIONAL_OPERATORS_MAP: Record<
  ConditionalOperators,
  U21SelectOptionProps
> = {
  [ConditionalOperators.GT]: { value: ConditionalOperators.GT, text: '>' },
  [ConditionalOperators.LT]: { value: ConditionalOperators.LT, text: '<' },
  [ConditionalOperators.LE]: {
    value: ConditionalOperators.LE,
    text: '<=',
  },
  [ConditionalOperators.GE]: {
    value: ConditionalOperators.GE,
    text: '>=',
  },
  [ConditionalOperators.EQ]: {
    value: ConditionalOperators.EQ,
    text: 'is equal to',
  },
  [ConditionalOperators.ANY_EQ]: {
    value: ConditionalOperators.EQ,
    text: 'is equal to',
  },
  [ConditionalOperators.NEQ]: {
    value: ConditionalOperators.NEQ,
    text: 'is not equal to',
  },
  [ConditionalOperators.NONE_EQ]: {
    value: ConditionalOperators.NEQ,
    text: 'is not equal to',
  },
  [ConditionalOperators.IN]: { value: ConditionalOperators.IN, text: 'is in' },
  [ConditionalOperators.ANY_IN]: {
    value: ConditionalOperators.IN,
    text: 'is in',
  },
  [ConditionalOperators.NOT_IN]: {
    value: ConditionalOperators.NOT_IN,
    text: 'is not in',
  },
  [ConditionalOperators.NONE_IN]: {
    value: ConditionalOperators.NOT_IN,
    text: 'is not in',
  },
  [ConditionalOperators.LIKE]: {
    value: ConditionalOperators.LIKE,
    text: 'is like',
  },
  [ConditionalOperators.ILIKE]: {
    value: ConditionalOperators.ILIKE,
    text: 'is like (case insensitive)',
  },
  [ConditionalOperators.ANY_LIKE]: {
    value: ConditionalOperators.LIKE,
    text: 'is like',
  },
  [ConditionalOperators.NOT_LIKE]: {
    value: ConditionalOperators.NOT_LIKE,
    text: 'is not like',
  },
  [ConditionalOperators.NOT_ILIKE]: {
    value: ConditionalOperators.NOT_ILIKE,
    text: 'is not like (case insensitive)',
  },
  [ConditionalOperators.NONE_LIKE]: {
    value: ConditionalOperators.NOT_LIKE,
    text: 'is not like',
  },
  [ConditionalOperators.BETWEEN]: {
    value: ConditionalOperators.BETWEEN,
    text: 'is between',
  },
  [ConditionalOperators.IS]: {
    value: ConditionalOperators.IS,
    text: 'is',
  },
  [ConditionalOperators.IS_EMPTY]: {
    value: ConditionalOperators.IS_EMPTY,
    text: 'is empty',
    tooltip: "Is empty will check if this field is 'NULL' or empty.",
  },
  [ConditionalOperators.IS_NOT_EMPTY]: {
    value: ConditionalOperators.IS_NOT_EMPTY,
    text: 'is not empty',
    tooltip:
      "Is not empty will check if this field is not 'NULL' or empty. Any value that is not 'NULL' or empty will flag.",
  },
  [ConditionalOperators.ANY_IS_EMPTY]: {
    value: ConditionalOperators.ANY_IS_EMPTY,
    text: 'is empty',
    tooltip: "Is empty will check if this field is 'NULL' or empty.",
  },
  [ConditionalOperators.NONE_IS_EMPTY]: {
    value: ConditionalOperators.NONE_IS_EMPTY,
    text: 'is not empty',
    tooltip:
      "Is not empty will check if this field is not 'NULL' or empty. Any value that is not 'NULL' or empty will flag.",
  },
  [ConditionalOperators.ILIKE_CONTAINS]: {
    value: ConditionalOperators.ILIKE_CONTAINS,
    text: 'contains',
    tooltip:
      'Checks if the field contains any of the selected values. Case insensitive.',
  },
  [ConditionalOperators.STARTS_WITH]: {
    value: ConditionalOperators.STARTS_WITH,
    text: 'starts with',
    tooltip:
      'Checks if the field starts with any of the selected values. Case insensitive.',
  },
  [ConditionalOperators.ENDS_WITH]: {
    value: ConditionalOperators.ENDS_WITH,
    text: 'ends with',
    tooltip:
      'Checks if the field ends with any of the selected values. Case insensitive.',
  },
  [ConditionalOperators.NOT_ILIKE_CONTAINS]: {
    value: ConditionalOperators.NOT_ILIKE_CONTAINS,
    text: 'does not contain',
    tooltip:
      'Checks if the field does not contain any of the selected values. Case insensitive.',
  },
  [ConditionalOperators.NOT_STARTS_WITH]: {
    value: ConditionalOperators.NOT_STARTS_WITH,
    text: 'does not start with',
    tooltip:
      'Checks if the field does not start with any of the selected values. Case insensitive.',
  },
  [ConditionalOperators.NOT_ENDS_WITH]: {
    value: ConditionalOperators.NOT_ENDS_WITH,
    text: 'does not end with',
    tooltip:
      'Checks if the field does not end with any of the selected values. Case insensitive.',
  },
};

export const IS_OPERAND_OPTIONS: U21SelectOptionProps[] = [
  {
    value: 'NULL',
    text: 'NULL',
  },
];

const DATE_OPERATOR_OPTIONS: U21SelectOptionProps[] = [
  {
    value: ConditionalOperators.GT,
    text: 'is after',
  },
  {
    value: ConditionalOperators.GE,
    text: 'is on or after',
  },
  {
    value: ConditionalOperators.LT,
    text: 'is before',
  },
  {
    value: ConditionalOperators.LE,
    text: 'is on or before',
  },
  {
    value: ConditionalOperators.EQ,
    text: 'is on',
  },
  {
    value: ConditionalOperators.NEQ,
    text: 'is not on',
  },
  CONDITIONAL_OPERATORS_MAP.BETWEEN,
  CONDITIONAL_OPERATORS_MAP.IS,
];

const NUMBER_OPERATOR_OPTIONS: U21SelectOptionProps[] = [
  {
    value: ConditionalOperators.EQ,
    text: '=',
  },
  {
    value: ConditionalOperators.NEQ,
    text: '!=',
  },
  CONDITIONAL_OPERATORS_MAP.GT,
  CONDITIONAL_OPERATORS_MAP.LT,
  CONDITIONAL_OPERATORS_MAP.GE,
  CONDITIONAL_OPERATORS_MAP.LE,
  CONDITIONAL_OPERATORS_MAP.BETWEEN,
  CONDITIONAL_OPERATORS_MAP.IS,
];

const BOOLEAN_OPERATOR_OPTIONS: U21SelectOptionProps[] = [
  {
    value: ConditionalOperators.EQ,
    text: '=',
  },
  CONDITIONAL_OPERATORS_MAP.IS,
];

const EMPTY_OPERATOR_OPTIONS: U21SelectOptionProps[] = [
  CONDITIONAL_OPERATORS_MAP.IS_EMPTY,
  CONDITIONAL_OPERATORS_MAP.IS_NOT_EMPTY,
];

const LIST_OPERATOR_OPTIONS: U21SelectOptionProps[] = [
  CONDITIONAL_OPERATORS_MAP.IN,
  CONDITIONAL_OPERATORS_MAP.NOT_IN,
  CONDITIONAL_OPERATORS_MAP.IS,
];

const STRING_OPERATOR_OPTIONS: U21SelectOptionProps[] = [
  CONDITIONAL_OPERATORS_MAP.EQ,
  CONDITIONAL_OPERATORS_MAP.NEQ,
  CONDITIONAL_OPERATORS_MAP.IN,
  CONDITIONAL_OPERATORS_MAP.NOT_IN,
  CONDITIONAL_OPERATORS_MAP.STARTS_WITH,
  CONDITIONAL_OPERATORS_MAP.NOT_STARTS_WITH,
  CONDITIONAL_OPERATORS_MAP.ENDS_WITH,
  CONDITIONAL_OPERATORS_MAP.NOT_ENDS_WITH,
  CONDITIONAL_OPERATORS_MAP.ILIKE_CONTAINS,
  CONDITIONAL_OPERATORS_MAP.NOT_ILIKE_CONTAINS,
  CONDITIONAL_OPERATORS_MAP.ILIKE,
  CONDITIONAL_OPERATORS_MAP.NOT_ILIKE,
  CONDITIONAL_OPERATORS_MAP.LIKE,
  CONDITIONAL_OPERATORS_MAP.NOT_LIKE,
  CONDITIONAL_OPERATORS_MAP.IS,
];

export const MATCHLIST_OPERATOR_OPTIONS: U21SelectOptionProps[] = [
  CONDITIONAL_OPERATORS_MAP.IN,
  CONDITIONAL_OPERATORS_MAP.NOT_IN,
];

export const IN_OPERATORS = new Set<string>([
  ConditionalOperators.IN,
  ConditionalOperators.ANY_IN,
  ConditionalOperators.NOT_IN,
  ConditionalOperators.NONE_IN,
  ConditionalOperators.STARTS_WITH,
  ConditionalOperators.NOT_STARTS_WITH,
  ConditionalOperators.ENDS_WITH,
  ConditionalOperators.NOT_ENDS_WITH,
  ConditionalOperators.ILIKE_CONTAINS,
  ConditionalOperators.NOT_ILIKE_CONTAINS,
]);

export const OPERAND_TYPE_OPERATOR_OPTIONS_MAP: Record<
  string,
  U21SelectOptionProps[]
> = {
  boolean: BOOLEAN_OPERATOR_OPTIONS,
  date: DATE_OPERATOR_OPTIONS,
  datetime: DATE_OPERATOR_OPTIONS,
  number: NUMBER_OPERATOR_OPTIONS,
  string: STRING_OPERATOR_OPTIONS,
  text: STRING_OPERATOR_OPTIONS,
  enum: STRING_OPERATOR_OPTIONS,
  list: LIST_OPERATOR_OPTIONS,
  empty: EMPTY_OPERATOR_OPTIONS,
};

export const LEGACY_SEQUENCE_RULES: string[] = [
  'sell_at_loss_sequence_embedded_filters',
  'relative_transaction_amount_sequence_embedded_filters',
];
export const ACH_RISK_SCORE_ENABLED_SCENARIO_RULES: string[] = [
  'simple_filters',
];
export const SCENARIO_CONSORTIUM_NEW_ACCOUNT_NAME =
  'consortium_new_account_monitoring';
export const SCENARIO_CONSORTIUM_ONGOING_ACCOUNT_NAME =
  'consortium_ongoing_account_monitoring';
export const CONSORTIUM_RULES: string[] = [
  SCENARIO_CONSORTIUM_NEW_ACCOUNT_NAME,
  SCENARIO_CONSORTIUM_ONGOING_ACCOUNT_NAME,
];

export const DORMANT_ACTIVITY_NAME = 'dormant_activity_embedded_filters';

export const SCENARIO_DUPLICATE_SERIAL_NUMBER_NAME = 'duplicate_serial_number';
export const SCENARIO_MISSING_SERIAL_NUMBER_NAME = 'missing_serial_number';
export const SCENARIO_CHECK_MATCHES_DEPOSIT_NAME =
  'check_matches_recent_deposit';
export const SCENARIO_TARDY_CHECK_NAME = 'tardy_check';
export const SCENARIO_CHECK_OUT_OF_RANGE_NAME = 'serial_number_out_of_range';
export const SCENARIO_BUILDER_CHECK_FRAUD_RULES: string[] = [
  SCENARIO_CHECK_OUT_OF_RANGE_NAME,
  SCENARIO_CHECK_MATCHES_DEPOSIT_NAME,
  SCENARIO_TARDY_CHECK_NAME,
];
export const CHECK_FRAUD_RULES_ALL: string[] = [
  SCENARIO_DUPLICATE_SERIAL_NUMBER_NAME,
  SCENARIO_MISSING_SERIAL_NUMBER_NAME,
  ...SCENARIO_BUILDER_CHECK_FRAUD_RULES,
];

export const SCENARIO_MULTIPLE_FAILED_VALIDATION_ATTEMPTS_NAME =
  'multiple_failed_validation_attempts';
export const SCENARIO_ACH_RISK_SCORE_NAME = 'ach_risk_score';
export const SCENARIO_ACH_CONSORTIUM_NAME =
  'consortium_flagged_ach_originator_or_receiver';
export const SCENARIO_ACH_MISMATCHED_NAME = 'ach_mismatched_account_info';
export const SCENARIO_ACH_DUPLICATE_NAME = 'duplicate_ach_transaction';
export const SCENARIO_SEQUENCE_ROLLING_WINDOW_NAME = 'sequence_rolling_window';
export const SCENARIO_SEQUENCE_CUMULATIVE_AGGREGATE_NAME =
  'sequence_cumulative_aggregate';
export const SCENARIO_SEQUENCE_CUMULATIVE_AGGREGATE_USPS_NAME =
  'sequence_cumulative_aggregate_usps';
export const SCENARIO_SEQUENCE_DAILY_AGGREGATE_NAME =
  'sequence_daily_aggregate';
export const SCENARIO_SEQUENCE_ROLLING_WINDOW_ONE_STEP_NAME =
  'sequence_rolling_window_one_step';
export const SCENARIO_SEQUENCE_ROLLING_WINDOW_WITH_COUNT_NAME =
  'sequence_rolling_window_with_count';
export const SCENARIO_SEQUENCE_ROLLING_WINDOW_ROUND_TRIP_NAME =
  'sequence_rolling_window_round_trip';
export const SCENARIO_WATCHLIST_NAME = 'watchlist';
export const EMPTY_CHECK_VALUE = 'EMPTY_CONSTANT_VALUE';

export const EMPTY_FIELD_VALUE: SymbolFieldValue = {
  type: ValueTypes.FIELD,
  field: '',
  label: '',
  model: '',
  datatype: '',
};

export enum AndOrOperator {
  AND = 'AND',
  OR = 'OR',
}

export const EMPTY_VARIABLE_VALUE: VariableValue = {
  type: ValueTypes.VARIABLE,
  name: '',
};

export const EMPTY_MATCHLIST_FIELD_VALUE: MatchlistFieldValue = {
  type: ValueTypes.MATCHLIST_FIELD,
  matchlist_type: 'STRING',
  matchlist_id: -1,
};

export const EMPTY_CONSTANT_VALUE: ConstantValue = {
  type: ValueTypes.CONSTANT,
  value: '',
};

export const EMPTY_CONSTANT_LIST_VALUE: ConstantValue = {
  type: ValueTypes.CONSTANT,
  value: [],
};

export const DEFAULT_CONSTANT_BOOLEAN: ConstantValue = {
  type: ValueTypes.CONSTANT,
  value: 'false',
};

export const EMPTY_DURATION_VALUE: DurationValue = {
  type: ValueTypes.DURATION,
  value: 0,
  unit: DmDurationUnits.DAY,
};

export const EMPTY_VALUE: ConstantValue = {
  type: ValueTypes.CONSTANT,
  value: EMPTY_CHECK_VALUE,
};

export enum MetricTypeEnum {
  CONSTANT = 'CONSTANT',
  TIMESERIES = 'TIMESERIES',
  PIE = 'PIE',
}

// Note: This enum value must be the capitalised version of the backend rule metric enum
export enum MetricNameEnum {
  OPEN_ALERTS = 'OPEN_ALERTS',
  CLOSED_ALERTS = 'CLOSED_ALERTS',
  TOTAL_ALERTS = 'TOTAL_ALERTS',
  ALERTS_IN_LAST_7_DAYS = 'ALERTS_IN_LAST_7_DAYS',
  ALERTS_IN_LAST_30_DAYS = 'ALERTS_IN_LAST_30_DAYS',
  ALERTS_CREATED_OVER_TIME = 'ALERTS_CREATED_OVER_TIME',
  OPEN_CASES = 'OPEN_CASES',
  CLOSED_CASES = 'CLOSED_CASES',
  TOTAL_CASES = 'TOTAL_CASES',
  SARS_FILED = 'SARS_FILED',
  CLOSED_ALERT_TO_CASE = 'CLOSED_ALERT_TO_CASE',
  CLOSED_ALERT_TO_SAR = 'CLOSED_ALERT_TO_SAR',
  AVERAGE_ALERTS_DISPOSITIONED_DAILY = 'AVERAGE_ALERTS_DISPOSITIONED_DAILY',
  DISPOSITION_TRUE_POSITIVE = 'DISPOSITION_TRUE_POSITIVE',
  DISPOSITION_FALSE_POSITIVE_HELPFUL = 'DISPOSITION_FALSE_POSITIVE_HELPFUL',
  DISPOSITION_FALSE_POSITIVE = 'DISPOSITION_FALSE_POSITIVE',
  DISPOSITION_BREAKDOWN = 'DISPOSITION_BREAKDOWN',
  DISPOSITION_TITLE_BREAKDOWN = 'DISPOSITION_TITLE_BREAKDOWN',
}

export enum MetricIntervalEnum {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  QUARTER = 'QUARTER',
  YEAR = 'YEAR',
  ALL = 'ALL',
}

export enum AlertsOverTimeIntervalEnum {
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
  ALL = 'ALL',
}

export const ALERTS_OVER_TIME_INTERVALS: U21ButtonGroupButtonProps<AlertsOverTimeIntervalEnum>[] =
  [
    {
      label: 'Weekly',
      value: AlertsOverTimeIntervalEnum.WEEK,
    },
    {
      label: 'Monthly',
      value: AlertsOverTimeIntervalEnum.MONTH,
    },
    {
      label: 'Yearly',
      value: AlertsOverTimeIntervalEnum.YEAR,
    },
    {
      label: 'All Time',
      value: AlertsOverTimeIntervalEnum.ALL,
    },
  ];

export const GRAPH_BASED_RULE_NAME = 'entity_link_counts';
export const WATCHLIST_RULE_NAME = 'watchlist';
export const DYNAMIC_RULE_NAME = 'custom_scenario';
export const EDD_PERIODIC_REVIEW_RULE_NAME = 'edd_periodic_review';

export const EDD_ACTIVATION_START_TIME_HELP =
  'For EDD rules, "Start time" doesn\'t control when the rule runs. It sets how far back to look for overdue entities on the first run. Instead, the rule begins running when activated.';

export const RULE_GRAPH_CYTOSCAPE_OPTIONS: CytoscapeOptions = {
  ...BASE_CYTOSCAPE_OPTIONS,
  layout: { name: 'cose', animate: false, nodeDimensionsIncludeLabels: true },
  style: [
    {
      selector: 'node',
      style: {
        ...BASE_NODE_STYLES,
        'background-image': (node) =>
          node.data('nodeType') === 'entity'
            ? User
            : CONNECTION_TYPE_TO_ICON[node.data('linkType')],
        'background-color': (node) => node.data('color'),
        content: (node) => node.data('label'),
      },
    },
    { selector: 'edge', style: { ...BASE_EDGE_STYLES } },
    {
      selector: 'node[label]',
      style: {
        ...BASE_LABEL_STYLES,
        'text-wrap': 'wrap',
      },
    },
  ],
};

export const MAX_DROPDOWN_OPTION_DESCRIPTION_LENGTH = 128;

export const EMPTY_RTM_COUNT_RESPONSE: RealTimeMonitoringCount = {
  rtr_flag_action_count: {},
  rtr_flag_action_count_without_filtering: 0,
  rtr_flag_transaction_count: {},
  rtr_flag_transaction_count_without_filtering: 0,
};

export const SCENARIO_BUILDER_FORM = 'scenario-builder-form';
export const SCENARIOS_SUPPORTING_FORM_BUILDER = [
  SCENARIO_SEQUENCE_CUMULATIVE_AGGREGATE_NAME,
  SCENARIO_SEQUENCE_ROLLING_WINDOW_NAME,
  SCENARIO_SEQUENCE_ROLLING_WINDOW_WITH_COUNT_NAME,
  SCENARIO_SEQUENCE_ROLLING_WINDOW_ROUND_TRIP_NAME,
  SCENARIO_SEQUENCE_DAILY_AGGREGATE_NAME,
  SCENARIO_SEQUENCE_ROLLING_WINDOW_ONE_STEP_NAME,
  SCENARIO_SEQUENCE_CUMULATIVE_AGGREGATE_USPS_NAME,
  SCENARIO_WATCHLIST_NAME,
  SCENARIO_ACH_CONSORTIUM_NAME,
  SCENARIO_ACH_DUPLICATE_NAME,
  SCENARIO_ACH_MISMATCHED_NAME,
  SCENARIO_DUPLICATE_SERIAL_NUMBER_NAME,
  SCENARIO_MISSING_SERIAL_NUMBER_NAME,
  SCENARIO_MULTIPLE_FAILED_VALIDATION_ATTEMPTS_NAME,
  SCENARIO_ACH_RISK_SCORE_NAME,
  ...CONSORTIUM_RULES,
];

export enum RuleTooltips {
  DEPRECATED_OPERATOR_USAGE = 'This rule uses an operator no longer supported in basic real-time rules (average or percentage). Create a velocity real-time rule to continue or contact Support for help.',
  LEGACY_RTR_CANNOT_BE_DUPLICATED = 'Duplication is disabled for legacy Real-time monitoring rules',
  NO_PERMISSIONS_TO_ACTIVATE_ERROR_MESSAGE = 'You do not have permissions to activate rules in live mode.',
  NO_PERMISSIONS_TO_ARCHIVE_ERROR_MESSAGE = 'You do not have permissions to archive live rules.',
  OVER_LIMIT_SUBTYPES = 'You have reached the limit for every event type. You must deactivate at least one of your existing rules to duplicate this one.',
  PENDING_SHADOW_ACTIVATION = "This shadow rule's activation is still pending.",
  RULE_IS_ARCHIVED = 'This rule is archived.',
  FLOW_RULE_IN_USE_CANT_BE_ARCHIVED = 'Nodes cannot be archived while in a Flow.',
  RTR_FLOW_RULE_IN_ACTIVE_FLOW_CANT_EDIT = 'Node cannot be edited after it is used by an active Flow.',
  RULE_IS_BEING_ARCHIVED = 'This rule is being archived.',
  VALIDATION_FAILED = 'Rule failed validation.',
  VALIDATION_INCOMPLETE = "This rule's validation is incomplete, with the following status: ",
  WATCHLIST_RULE_ACTIVATION = 'Please reach out to your Customer Success Manager to activate watchlist rules.',
}

export enum RuleConditionTooltips {
  VIEW_RULE_CONDITIONS = 'Why did this flag?',
  RULE_CONDITIONS_NOT_AVAILABLE = 'This update was created before May 1st, 2025. Rule condition breakdowns are only available for updates created after this date using the dynamic rule builder.',
}

// TODO: chore/sc-97802/fe-move-dropdown-builder-specific-constants
export enum DropdownBuilderFormFields {
  CONDITION = 'condition',
  CONDITIONS = 'conditions',
  FACTS = 'facts',
  LEFT_OPERAND = 'leftOperand',
  OPERATOR = 'operator',
}

export const EMPTY_FACT_ADVANCED_CONDITION: AdvancedCondition = {
  type: CompositeTypes.COMPOSITE,
  operator: ConditionalOperators.EQ,
  meta_ui: { input_choice: UiInputs.PLAIN_TEXT },
  fieldText: '',
};

export const EMPTY_ADVANCED_CONDITION: AdvancedCondition = {
  type: CompositeTypes.RULE_CONDITION_COMPOSITE,
  operator: ConditionalOperators.EQ,
  meta_ui: { input_choice: UiInputs.PLAIN_TEXT },
  fieldText: '',
};

export const EMPTY_FACT_DROPDOWN_BUILDER_CONDITION: Condition = {
  type: CompositeTypes.COMPOSITE,
  operator: ConditionalOperators.EQ,
  meta_ui: { input_choice: UiInputs.DROPDOWN },
  leftOperand: EMPTY_FIELD_VALUE,
  rightOperand: null,
  betweenOperand: null,
};

export const EMPTY_DROPDOWN_BUILDER_CONDITION: Condition = {
  type: CompositeTypes.RULE_CONDITION_COMPOSITE,
  operator: ConditionalOperators.EQ,
  meta_ui: { input_choice: UiInputs.DROPDOWN },
  leftOperand: EMPTY_FIELD_VALUE,
  rightOperand: null,
  betweenOperand: null,
};

export const EMPTY_FACT_DROPDOWN_BUILDER_AND_OR_CONDITION: AndOrCondition = {
  type: CompositeTypes.COMPOSITE,
  operator: AndOrOperator.AND,
  conditions: [EMPTY_FACT_DROPDOWN_BUILDER_CONDITION],
};

export const EMPTY_DROPDOWN_BUILDER_AND_OR_CONDITION: AndOrCondition = {
  type: CompositeTypes.RULE_CONDITION_COMPOSITE,
  operator: AndOrOperator.AND,
  conditions: [EMPTY_DROPDOWN_BUILDER_CONDITION],
};

export const EMPTY_FACT_DROPDOWN_BUILDER_AND_OR_CONDITION_WITH_NO_CONDITIONS: AndOrCondition =
  {
    type: CompositeTypes.COMPOSITE,
    operator: AndOrOperator.AND,
    conditions: [],
  };

export const EMPTY_DROPDOWN_BUILDER_AND_OR_CONDITION_WITH_NO_CONDITIONS: AndOrCondition =
  {
    type: CompositeTypes.RULE_CONDITION_COMPOSITE,
    operator: AndOrOperator.AND,
    conditions: [],
  };

export const EMPTY_SCENARIO_DROPDOWN_BUILDER_AND_OR_CONDITION: ScenarioAndOrCondition =
  {
    type: CompositeTypes.RULE_CONDITION_COMPOSITE,
    operator: AndOrOperator.AND,
    conditions: [EMPTY_DROPDOWN_BUILDER_CONDITION],
  };

export const EMPTY_SCENARIO_DROPDOWN_BUILDER_AND_OR_CONDITION_WITH_NO_CONDITIONS: ScenarioAndOrCondition =
  {
    type: CompositeTypes.RULE_CONDITION_COMPOSITE,
    operator: AndOrOperator.AND,
    conditions: [],
  };

export const DEFAULT_DROPDOWN_BUILDER_FORM_VALUES: DropdownBuilderFormValues =
  Object.freeze({
    facts: [],
    condition: {
      type: CompositeTypes.RULE_CONDITION_COMPOSITE,
      operator: AndOrOperator.AND,
      conditions: [EMPTY_DROPDOWN_BUILDER_CONDITION],
    },
  });

export const DEFAULT_DROPDOWN_BUILDER_FORM_VALUES_WITH_NO_CONDITIONS: DropdownBuilderFormValues =
  Object.freeze({
    facts: [],
    condition: {
      type: CompositeTypes.RULE_CONDITION_COMPOSITE,
      operator: AndOrOperator.AND,
      conditions: [],
    },
  });
//
export const CUSTOM_DATETIME_FIELDS = ['event_time', 'business_date'];

// CTR specific logic
export const BUSINESS_DATE_FIELD_VALUE: FieldValue = {
  field: 'business_date',
  datatype: 'datetime',
  type: ValueTypes.FIELD,
  model: 'txn_event',
};
export const BUSINESS_DATE_FIELD_DESCRIPTION =
  'Business date uses custom logic aligned with FinCEN CTR guidance and may override other settings. Transactions outside of business hours will not be included until the next business day.';

export enum AlertUpdateTooltips {
  NOT_SUPPORTED_MESSAGE = 'Why did this flag is only supported for alerts flagged by Dynamic Rules after May 1st, 2025.',
  GENERAL_MESSAGE = 'This section explains why the alert was triggered and how it’s been updated. When a rule detects new activity that matches an existing open alert, the system adds an update to the alert. Each row represents a time the rule matched new activity.',
}

export const RULE_STATUS_FRIENDLY_NAME: Record<RuleStatuses, string> = {
  ACTIVE: 'Active',
  ACTIVE_SHADOW: 'Shadow',
  ARCHIVED: 'Archived',
  EXPIRED: 'Expired',
  PENDING_ACTIVATION: 'Pending activation',
  PENDING_SHADOW_ACTIVATION: 'Pending shadow activation',
  PUBLISHED: 'Published',
  SHADOW_MIGRATION: 'Shadow migration',
  VALIDATION: 'Validation',
};
export const VALIDATION_INFO_ITEM_LABEL_WIDTH = '110px';

export const TEXT_RULE_PROMPT_EXAMPLES = [
  {
    title: 'Historical Deviation',
    prompt:
      "Build a historical deviation rule to catch unusual spending spikes. Flag an account if the total amount they sent in the last 7 days is more than 3 times the total amount they sent over the last 60 days. Only evaluate transactions with a 'completed' status.",
  },
  {
    title: 'Account Takeover',
    prompt:
      "Set up a multi-signal rule for suspected account takeovers. Flag a user if they have more than 3 'failed_login' actions in the last 24 hours, AND they have sent over $5,000 in completed transactions in the last hour. Make sure this only flags users whose profile country is NOT 'US'.",
  },
  {
    title: 'Unique Connections',
    prompt:
      "Create a velocity rule to catch potential money mules. Flag any sender who has successfully transferred money to more than 15 unique receivers in the last 7 days. I only want to count transactions where the status is 'completed', the currency is 'USD', and the individual transaction amount is greater than $50.",
  },
];

export const EXPERIMENTAL_OPERATORS = ['AI_FUZZY_SCORE'];

export const EXPERIMENTAL_OPERATOR_WARNING =
  'The AI fuzzy matching operator is still experimental; verify results for accuracy. Additional charges will apply (free until May 1, 2026). Rule execution interval limited to 1 day or longer. Rule failures will not be automatically retried.';

export const EXPERIMENTAL_OPERATOR_MIN_INTERVAL_MINUTES = 1440; // 1 day = 1440 minutes

export const DECISION_ICON_MAP: Record<RtrDecision, TablerIcon> = {
  [RtrDecision.ALLOW]: IconCircleCheck,
  [RtrDecision.DENY]: IconBan,
  [RtrDecision.ERROR]: IconAlertTriangle,
  [RtrDecision.STEP_UP_AUTHENTICATION]: IconShieldLock,
};
